import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';

import { useAppDispatch, useAppSelector } from '@/store';
import { authStore } from '@/store/auth';
import { pages } from '@/settings/pages';
import { adminsApi } from '@/services/api';
import { useFlashMessenger } from '@/hooks/useFlashMessenger';
import { useApiErrorHandler } from '@/hooks/useApiErrorHandler';
import { FLASH_MESSAGE_LIFETIME_MILLISEC } from '@/constants';

import { PasswordEditForm } from '../Presentation';
import { Loading } from '@/components/utils/Loading';
import { useCheckReAuthenticationExpiration } from '@/hooks/useCheckReAuthenticationExpiration';

export const PasswordEditFormContainer: React.FC = () => {
  const { apiErrorHandler } = useApiErrorHandler();
  const dispatch = useAppDispatch();
  const { flashNotice, flashError } = useFlashMessenger();
  const admin = useAppSelector(authStore.selectors.meSelector);
  const [isLoading, setIsLoading] = useState(false);
  const [mismatchError, setMismatchError] = useState<string>();
  const logoutTimeoutRef = useRef<NodeJS.Timeout>();

  const { t } = useTranslation();

  const checkReAuthenticationExpiration = useCheckReAuthenticationExpiration(
    setIsLoading,
    pages.account.password.edit,
  );

  useEffect(() => {
    checkReAuthenticationExpiration();
  }, [checkReAuthenticationExpiration]);

  useEffect(() => {
    return () => {
      if (logoutTimeoutRef.current) {
        clearTimeout(logoutTimeoutRef.current);
      }
    };
  }, []);

  const onSubmit = useCallback(
    async (currentPassword: string, newPassword: string) => {
      try {
        setMismatchError(undefined);
        setIsLoading(true);
        await adminsApi.updatePassword({
          data: {
            currentPassword,
            newPassword,
            adminId: admin.id,
          },
        });
        // パスワード変更成功後、メッセージを表示してからログアウトフローを実行
        // パスワード変更時には再ログインさせるためにバックエンドで強制的にセッションを破棄しているが、
        // 防御的にフロントからも明示的にログアウトエンドポイントを叩いておく
        flashNotice(t('text.account.password.edit.done'));
        logoutTimeoutRef.current = setTimeout(() => {
          dispatch(authStore.asyncActions.logout());
        }, FLASH_MESSAGE_LIFETIME_MILLISEC);
      } catch (err) {
        if (axios.isAxiosError(err) && err.response !== undefined) {
          if (err.response && err.response.status === 400) {
            setMismatchError(t('ui.password.current.error.incorrect'));
          } else if (
            err.code !== 'ECONNABORTED' &&
            err.response &&
            err.response.status === undefined
          ) {
            // timeout以外の原因でresponseがない場合
            flashError(t('ui.error.network_error'));
          } else {
            apiErrorHandler(err);
          }
          setIsLoading(false);
        }
      }
    },
    [admin.id, apiErrorHandler, dispatch, flashNotice, flashError, t],
  );

  const childProps = { isLoading, mismatchError, onSubmit };
  return isLoading ? <Loading /> : <PasswordEditForm {...childProps} />;
};
